import { Tooltip } from 'antd'
import { useEffect, useState, useRef } from 'react'
import WizrInput from '../WizrInput'
import WizrTextArea from '../WizrTextArea'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrFlexLayout from '../WizrFlexLayout'
import { styled } from 'styled-components'
import { useContentLoadingSelector, useTagsSelector } from '../../redux/selectors/content'
import WizrTagInput from '../WizrTagInput'
import { dispatchTags } from '../../redux/actions/content'
import WizrText from '../WizrText'
import InfoIcon from '../../assets/icons/Info'
import WizrLoader from '../WizrLoader'

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
  width: 100%;
  position: relative;
`

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
  width: 100%;
`

const TagInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

interface IAddName {
  name?: string
  description?: string
  tags?: string[]
  onFormSubmission: (params: { [key: string]: any }) => void
  onCancel: () => void
}

const AddName = ({
  name,
  description,
  tags,
  onFormSubmission,
  onCancel,
}: IAddName): JSX.Element => {
  const loading = useContentLoadingSelector()
  const [isTagsLoading, setIsTagsLoading] = useState(true)
  
  // Form state
  const [formState, setFormState] = useState({
    currentname: name || '',
    description: description || '',
    tags: tags || []
  })

  // Validation state
  const [showNameValidation, setShowNameValidation] = useState(false)
  const [showDescriptionValidation, setShowDescriptionValidation] = useState(false)

  // Refs for error scrolling
  const nameInputRef = useRef<HTMLDivElement>(null)
  const descriptionInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatchTags()
  }, [])

  const tagsList = useTagsSelector()
  const tagList = tagsList?.map((item: any) => ({
    label: item,
    value: item,
  }))

  useEffect(() => {
    if (tagsList !== undefined) {
      setIsTagsLoading(false)
    } else if (loading) {
      setIsTagsLoading(true)
    }
  }, [tagsList, loading])

  useEffect(() => {
    setFormState({
      currentname: name || '',
      description: description || '',
      tags: tags || []
    })
  }, [name, description, tags])

  const doUpdateFields = (fieldName: string, fieldValue: any) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: fieldValue
    }))
  }

  const validateAllFields = () => {
    const isBlank = (text?: string) => !text || text.trim() === ''
    let isValid = true
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null

    // Reset validation states
    setShowNameValidation(false)
    setShowDescriptionValidation(false)

    // Validate name
    if (isBlank(formState.currentname)) {
      setShowNameValidation(true)
      if (!firstErrorRef) firstErrorRef = nameInputRef
      isValid = false
    }

    // Validate description
    if (isBlank(formState.description)) {
      setShowDescriptionValidation(true)
      if (!firstErrorRef) firstErrorRef = descriptionInputRef
      isValid = false
    }

    // Scroll to first error
    if (!isValid && firstErrorRef?.current) {
      setTimeout(() => {
        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    return isValid
  }

  const handleSubmit = () => {
    if (!validateAllFields()) {
      return
    }

    onFormSubmission({
      currentname: formState.currentname,
      description: formState.description,
      tags: formState.tags
    })
  }

  return (
    <StyledContainer background='#FFF' justifyContent='space-evenly'>
      <div style={{ width: '100%', position: 'relative' }}>
        {loading && (
          <WizrLoader
            loading={true}
            fullPage={false}
            overlay={true}
          />
        )}
        <WizrFlexLayout
          flexDirection='column'
          justifyContent='flex-start'
          background='#fff'
          alignItems='flex-start'
          style={{ width: '100%', gap: '16px' }}
        >
        <InputContainer ref={nameInputRef}>
          <WizrText type='body2'>Name</WizrText>
          <WizrInput
            style={{ width: '100%' }}
            border="1px solid #000"
            background='#F2F4F9'
            onChange={(value: any) => {
              doUpdateFields('currentname', value)
              if (value && showNameValidation) {
                setShowNameValidation(false)
              }
            }}
            value={formState.currentname}
            placeholder="Enter name"
            showError={showNameValidation}
            errorMessage={showNameValidation ? "Please Enter a Name" : undefined}
            disabled={loading}
          />
        </InputContainer>

        <InputContainer ref={descriptionInputRef}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexDirection: 'row' }}>
            <WizrText type='body2'>Description</WizrText>
            <Tooltip
              title={
                <WizrText textColor="white" type="caption">
                  Enter description
                </WizrText>
              }
            >
              <InfoIcon />
            </Tooltip>
          </div>
          <WizrTextArea
            background='#F2F4F9'
            minRows={5}
            onChange={(value: any) => {
              doUpdateFields('description', value)
              if (value && showDescriptionValidation) {
                setShowDescriptionValidation(false)
              }
            }}
            value={formState.description}
            placeholder="Enter description"
            showError={showDescriptionValidation}
            errorMessage={showDescriptionValidation ? "Please Enter a Description" : undefined}
            disabled={loading}
          />
        </InputContainer>

        <InputContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexDirection: 'row' }}>
            <WizrText type='body2'>Tags</WizrText>
            <Tooltip
              title={
                <WizrText textColor="white" type="caption">
                  Select or create tags to categorize
                </WizrText>
              }
            >
              <InfoIcon />
            </Tooltip>
          </div>
          <TagInputWrapper>
            {!isTagsLoading && (
              <WizrTagInput
                placeHolder='Select tags'
                options={tagList}
                background='#F2F4F9'
                maxTagCount={'responsive'}
                onChange={(value: any) => doUpdateFields('tags', value)}
                value={formState.tags}
              />
            )}
            {isTagsLoading && (
              <WizrLoader
                loading={true}
                fullPage={false}
                overlay={false}
              />
            )}
          </TagInputWrapper>
        </InputContainer>

        <WizrFlexSpacing
          justifyContent='flex-end'
          flexDirection='row'
          size='large'
          background='#FFF'
        >
          <WizrButton type='default' text='Cancel' onClick={onCancel} style={{ borderRadius: '20px' }} disabled={loading} />
          <WizrButton text='Create' onClick={handleSubmit} disabled={loading} style={{ borderRadius: '20px' }} />
        </WizrFlexSpacing>
        </WizrFlexLayout>
      </div>
    </StyledContainer>
  )
}

export default AddName
